import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Image,
  Alert,
  ScrollView,
  Platform,
  ActivityIndicator,
} from "react-native";
import { Calendar } from "react-native-big-calendar";
import AsyncStorage from "@react-native-async-storage/async-storage";
// import { useNavigation } from "@react-navigation/native";
import axios from "axios";
import moment from "moment";
import API_PATHS from "../src/apiCalls/ApiPath";
import TopGreenHeader from "./screens/Common/TopGreenHeader";
import { isEventServiceType, eventTypeLabel } from "./screens/Common/helper";

// Fallback colors for event codes not matched exactly in the maps below
// (the backend format varies: EVENT-*, EVENTS_*, ...).
const eventBgColor = "#E94D8B20";
const eventTextColor = "#E94D8B";

const typeLabel = (type) =>
  bookingTypeDescriptions[type] || eventTypeLabel(type);
const typeBgColor = (type) =>
  bookingTypeColors[type] || (isEventServiceType(type) ? eventBgColor : undefined);
const typeTextColor = (type) =>
  textColor[type] || (isEventServiceType(type) ? eventTextColor : undefined);


const bookingTypeDescriptions = {
  HOMECARE: "Home Care",
  LEARNCARE: "At Learning Center",
  TRANSPORT: "Transportation",
  BIRTHDAY: "Birthday Party",
  VENREN: "Venue Rental",
  SOCIALHUB: "Social Hub",
  BREAKFASTCLUB: "Breakfast Club",
  "EVENT-STARTER": "Starter DIY Package",
  "EVENT-DELUX": "Deluxe Custom Packages",
  "EVENT-SIGNATURE": "Signature Curated Packages",
  "EVENT-PARTY_HELPER": "Party Helpers",
};

const bookingTypeColors = {
  HOMECARE: "#D1BA5A20",
  LEARNCARE: "#FFA50020",
  TRANSPORT: "#BCCB8420",
  BIRTHDAY: "#2A2AD020",
  VENREN: "#2A2AD020",
  SOCIALHUB: "#FFA50020",
  BREAKFASTCLUB: "#2A2AD020",
  "EVENT-STARTER": "#E94D8B20",
  "EVENT-DELUX": "#E94D8B20",
  "EVENT-SIGNATURE": "#E94D8B20",
  "EVENT-PARTY_HELPER": "#E94D8B20",
};

const textColor = {
  HOMECARE: "#D1BA5A",
  LEARNCARE: "#FFA500",
  TRANSPORT: "#BCCB84",
  BIRTHDAY: "#2A2AD0",
  VENREN: "#2A2AD0",
  SOCIALHUB: "#FFA500",
  BREAKFASTCLUB: "#2A2AD0",
  "EVENT-STARTER": "#E94D8B",
  "EVENT-DELUX": "#E94D8B",
  "EVENT-SIGNATURE": "#E94D8B",
  "EVENT-PARTY_HELPER": "#E94D8B",
};

const getDatesInRange = (startDate, endDate) => {
  const dates = [];
  let current = new Date(startDate);
  current.setHours(0, 0, 0, 0);
  const last = new Date(endDate);
  last.setHours(0, 0, 0, 0);
  while (current <= last) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
};



export default function CalendarPage({ navigation }) {
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // const navigation = useNavigation();

  const onEventPress = useCallback(
    (event) => {
      if (!event) return;
      const route = event.isRecurring ? "RecurringDetails" : "BookingDetails";
      const id = event.isRecurring ? { recurringId: event.id } : { bookingId: event.id };
      //console.log("id is ",event.id),
      navigation.navigate(route, id);
    },
    [navigation]
  );

  const fetchBookings = useCallback(async (year, month) => {
    const getLastDayOfMonth = (y, m) => new Date(y, m + 1, 0).getDate();
    setLoading(true);
    try {
      const token = await AsyncStorage.getItem("token");
      if (!token) {
        return;
      }

      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month, getLastDayOfMonth(year, month));

      const fromDate = Math.floor(startDate.getTime() / 1000);
      const toDate = Math.floor(endDate.getTime() / 1000);

      const response = await axios.post(
        `${API_PATHS.BASE_URL}${API_PATHS.CALENDAR_DATA}`,
        { fromDate, toDate },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const bookings = response.data.Data.bookings || [];
      const recurringBookings = response.data.Data.recurringBookings || [];

      const normalEvents = bookings.map((booking) => {
        const start = new Date(booking.bookingDateTime * 1000);
        return {
          id: booking.bookingId,
          title: typeLabel(booking.type) || "Unknown",
          start,
          end: new Date(start.getTime() + 60 * 60 * 1000),
          type: booking.type,
          isRecurring: false,
        };
      });

      const recurringEvents = recurringBookings.flatMap((booking) => {
        const bookingStart = new Date(booking.startDate * 1000);


        const bookingEnd = new Date(booking.startDate * 1000);
        if (
          bookingStart.getFullYear() === bookingEnd.getFullYear() &&
          bookingStart.getMonth() === bookingEnd.getMonth() &&
          bookingStart.getDate() === bookingEnd.getDate()
        ) {
          return [
            {
              id: `${booking.id}-${moment(bookingStart).format("YYYYMMDD")}`,
              title: typeLabel(booking.type) || "Learning Care/Transport",
              start: bookingStart,
              end: new Date(bookingStart.getTime() + 60 * 60 * 1000),
              type: booking.type,
              isRecurring: true,
            },
          ];
        } else {
          const dates = getDatesInRange(bookingStart, bookingEnd);
          return dates.map((date) => ({
            id: `${booking.id}-${moment(date).format("YYYYMMDD")}`,
            title: typeLabel(booking.type) || "Learning Care/Transport",
            start: date,
            end: new Date(date.getTime() + 60 * 60 * 1000),
            type: booking.type,
            isRecurring: true,
          }));
        }
      });

      const formattedEvents = [...normalEvents, ...recurringEvents].filter(
        (event) =>
          event.start &&
          event.end &&
          !isNaN(event.start.getTime()) &&
          !isNaN(event.end.getTime())
      );

      setEvents(formattedEvents);
    } catch (error) {
      //console.error(
      //"Error fetching bookings:",
      //error.response?.data || error.message
      //);
      Alert.alert("Error", "Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentDate && !isNaN(currentDate.getTime())) {
      fetchBookings(currentDate.getFullYear(), currentDate.getMonth());
    }
  }, [currentDate, fetchBookings]);

  const renderEvent = useCallback(
    (event) => {
      if (!event?.start || !event?.end) return null;
      const backgroundColor = event.isRecurring
        ? "#c12929"
        : typeBgColor(event.type) || "#666";
      const fontColor = event.isRecurring
        ? "#000"
        : typeTextColor(event.type) || "#666";
      return (
        <TouchableOpacity
          style={[
            styles.event,
            {
              backgroundColor,
              borderColor: fontColor,
              borderWidth: 1,
            },
          ]}
          onPress={() => onEventPress(event)}
        >
          <Text style={[styles.eventText, { color: fontColor }]}>
            {event.title}
          </Text>
        </TouchableOpacity>
      );
    },
    [onEventPress]
  );

  const onDayPress = useCallback(
    (date) => {
      if (!date) return;
      const selectedDate = moment(date).format("YYYY-MM-DD");
      const selectedEvents = events.filter(
        (event) => moment(event.start).format("YYYY-MM-DD") === selectedDate
      );
      if (selectedEvents.length === 1) {
        navigation.navigate("BookingDetails", {
          bookingId: selectedEvents[0].id,
        });
      } else if (selectedEvents.length > 1) {
        navigation.navigate("DetailsTab", { selectedDate });
      } else {
        Alert.alert("No Booking", "No bookings available for this date.");
      }
    },
    [events, navigation]
  );

  const onMonthChange = useCallback((date) => {
    if (!date) return;
    const newDate = new Date(date);
    if (isNaN(newDate.getTime())) return;
    newDate.setDate(1);
    setCurrentDate(newDate);
  }, []);

  return (
    <View style={styles.container}>
      {/* Header */}
      <TopGreenHeader Heading={"My Bookings"} navigation={navigation} isNotification={true} isBackIcon={true} />

      {/* Month Navigation */}
      <View style={styles.monthYearContainer}>
        <TouchableOpacity
          onPress={() => {
            const newDate = new Date(currentDate);
            newDate.setMonth(newDate.getMonth() - 1);
            onMonthChange(newDate);
          }}
        >
          <Text style={styles.navigationArrow}>{"<"}</Text>
        </TouchableOpacity>
        <Text style={styles.monthYearText}>
          {moment(currentDate).format("MMMM YYYY")}
        </Text>
        <TouchableOpacity
          onPress={() => {
            const newDate = new Date(currentDate);
            newDate.setMonth(newDate.getMonth() + 1);
            onMonthChange(newDate);
          }}
        >
          <Text style={styles.navigationArrow}>{">"}</Text>
        </TouchableOpacity>
      </View>

      {/* Calendar */}
      <ScrollView>
        {loading ? (
          <ActivityIndicator size="large" color="#A6CE39" style={styles.loader} />
        ) : (
          <Calendar
            events={events}
            height={600}
            mode="month"
            showTime={false}
            date={currentDate}
            onChangeDate={onMonthChange}
            renderEvent={renderEvent}
            onPressEvent={onEventPress}
            onPressCell={onDayPress}
            eventCellStyle={(event) => {
              const backgroundColor = event.isRecurring
                ? "#c12929"
                : typeBgColor(event.type) || "#666";
              const borderColor = event.isRecurring ? "#000" : typeTextColor(event.type) || "#666";
              return {
                backgroundColor,
                borderColor,
                borderWidth: 1,
                borderRadius: 4,
                padding: 2,
                marginVertical: 1,
              };
            }}
            calendarCellStyle={styles.calendarCell}
            calendarCellTextStyle={styles.calendarCellText}
          />
        )}
      </ScrollView>

      {/* Floating Action Button */}
      <TouchableOpacity
        style={styles.fabRight}
        onPress={() => navigation.navigate("DetailsTab")}
      >
        <Image style={styles.fabRight} source={require("./assets/bookinList1.png")} />
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  header: {
    backgroundColor: "#A6CE39",
    padding: 15,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerText: {
    fontFamily: Platform.OS === "web" ? "Facebook Sans" : "FacebookSansRegular",
    fontStyle: "normal",
    fontWeight: "bold",
    color: "#fff",
    fontSize: 28,
  },
  backIcon: {
    width: 15,
    height: 28,
  },
  notificationIcon: {
    width: 28,
    height: 30,
  },
  loader: {
    marginTop: "20%",
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  fabRight: {
    position: "absolute",
    bottom: 20,
    right: 15,
    width: 70,
    height: 70,
  },
  event: {
    padding: 2,
    borderRadius: 4,
    marginVertical: 1,
  },
  eventText: {
    fontFamily: Platform.OS === "web" ? "Facebook Sans" : "FacebookSansRegular",
    fontSize: 8,
    fontWeight: "normal",
  },
  monthYearContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 10,
    backgroundColor: "#f0f0f0",
  },
  monthYearText: {
    fontFamily: Platform.OS === "web" ? "Facebook Sans" : "FacebookSansBold",
    fontSize: 28,
    color: '#000'
  },
  navigationArrow: {
    fontSize: 34,
    fontWeight: "bold",
    color: "#000000",
  },
  calendarCell: {
    height: 130,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  calendarCellText: {
    fontSize: 20,
    fontFamily: Platform.OS === "web" ? "Facebook Sans" : "FacebookSansRegular",
    fontWeight: "bold",
  },
});
